import React, { useState, useEffect, useMemo } from 'react'
import Swal from 'sweetalert2'
import Select from 'react-select'
import apiClient from '../../config/api'

const AddProducts = () => {
  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    category: '',
    unit: '',
    initial_quantity: '',
    initial_purchase_price: '',
    initial_selling_price: '',
    supplier_id: '',
    image: null
  })
  const [categories, setCategories] = useState([])
  const [units, setUnits] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingData, setFetchingData] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchingData(true)
        const [categoriesResponse, unitsResponse, suppliersResponse] = await Promise.all([
          apiClient.get('/api/products/categories/'),
          apiClient.get('/api/products/units/'),
          apiClient.get('/api/stock/suppliers/')
        ])
        
        setCategories(categoriesResponse.data || [])
        setUnits(unitsResponse.data || [])
        setSuppliers(suppliersResponse.data || [])
      } catch (error) {
        console.error('Error fetching data:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load categories, units, or suppliers. Please refresh the page.'
        })
      } finally {
        setFetchingData(false)
      }
    }

    fetchData()
  }, [])

  // Transform data for react-select
  const categoryOptions = useMemo(() => {
    return categories.map(category => ({
      value: category.id,
      label: category.name
    }))
  }, [categories])

  const unitOptions = useMemo(() => {
    return units.map(unit => ({
      value: unit.id,
      label: unit.abbreviation ? `${unit.name} (${unit.abbreviation})` : unit.name
    }))
  }, [units])

  const supplierOptions = useMemo(() => {
    return suppliers.map(supplier => ({
      value: supplier.id,
      label: supplier.name
    }))
  }, [suppliers])

  // Get selected values for react-select
  const selectedCategory = useMemo(() => {
    return formData.category 
      ? categoryOptions.find(opt => opt.value === parseInt(formData.category))
      : null
  }, [formData.category, categoryOptions])

  const selectedUnit = useMemo(() => {
    return formData.unit 
      ? unitOptions.find(opt => opt.value === parseInt(formData.unit))
      : null
  }, [formData.unit, unitOptions])

  const selectedSupplier = useMemo(() => {
    return formData.supplier_id 
      ? supplierOptions.find(opt => opt.value === parseInt(formData.supplier_id))
      : null
  }, [formData.supplier_id, supplierOptions])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value === '' ? '' : value
    })
  }

  const handleSelectChange = (selectedOption, fieldName) => {
    setFormData({
      ...formData,
      [fieldName]: selectedOption ? selectedOption.value.toString() : ''
    })
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid File',
          text: 'Please select an image file.'
        })
        e.target.value = '' // Clear the input
        return
      }
      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        Swal.fire({
          icon: 'error',
          title: 'File Too Large',
          text: 'Image size should be less than 5MB.'
        })
        e.target.value = '' // Clear the input
        return
      }
      setFormData({
        ...formData,
        image: file
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Use FormData if image is present, otherwise use JSON
      const hasImage = formData.image !== null
      
      let payload
      let config = {}
      
      if (hasImage) {
        // Use FormData for file upload
        payload = new FormData()
        payload.append('sku', formData.sku.trim())
        payload.append('name', formData.name.trim())
        payload.append('initial_quantity', parseFloat(formData.initial_quantity))
        
        if (formData.category) {
          payload.append('category', parseInt(formData.category))
        }
        if (formData.unit) {
          payload.append('unit', parseInt(formData.unit))
        }
        if (formData.initial_purchase_price) {
          payload.append('initial_purchase_price', parseFloat(formData.initial_purchase_price))
        }
        if (formData.initial_selling_price) {
          payload.append('initial_selling_price', parseFloat(formData.initial_selling_price))
        }
        if (formData.supplier_id) {
          payload.append('supplier_id', parseInt(formData.supplier_id))
        }
        
        // Append image file
        payload.append('image', formData.image)
        
        // Set Content-Type header for multipart/form-data
        config.headers = {
          'Content-Type': 'multipart/form-data'
        }
      } else {
        // Use JSON payload when no image
        payload = {
          sku: formData.sku.trim(),
          name: formData.name.trim(),
          category: formData.category ? parseInt(formData.category) : null,
          unit: formData.unit ? parseInt(formData.unit) : null,
          initial_quantity: parseFloat(formData.initial_quantity),
          initial_purchase_price: formData.initial_purchase_price ? parseFloat(formData.initial_purchase_price) : null,
          initial_selling_price: formData.initial_selling_price ? parseFloat(formData.initial_selling_price) : null,
          supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null
        }

        // Remove null/empty values to let backend handle defaults
        Object.keys(payload).forEach(key => {
          if (payload[key] === null || payload[key] === '') {
            delete payload[key]
          }
        })
      }

      await apiClient.post('/api/products/products/', payload, config)

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Product added successfully!',
        confirmButtonText: 'OK'
      })

      // Reset form
      setFormData({
        name: '',
        sku: '',
        category: '',
        unit: '',
        initial_quantity: '',
        initial_purchase_price: '',
        initial_selling_price: '',
        supplier_id: '',
        image: null
      })
      
      // Reset file input
      const imageInput = document.getElementById('image')
      if (imageInput) {
        imageInput.value = ''
      }
    } catch (error) {
      console.error('Error creating product:', error)
      
      // Extract error message from response - simple approach
      let errorMessage = 'Failed to create product. Please try again.'
      
      // Check if we have a response with data
      if (error.response) {
        const errorData = error.response.data
        
        // Handle string responses
        if (typeof errorData === 'string') {
          errorMessage = errorData
        } else if (errorData && typeof errorData === 'object') {
          // Try to get the error message in order of priority
          if (errorData.message) {
            errorMessage = Array.isArray(errorData.message) ? errorData.message[0] : String(errorData.message)
          } else if (errorData.detail) {
            errorMessage = Array.isArray(errorData.detail) ? errorData.detail[0] : String(errorData.detail)
          } else if (errorData.error) {
            errorMessage = Array.isArray(errorData.error) ? errorData.error[0] : String(errorData.error)
          } else if (errorData.non_field_errors) {
            errorMessage = Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : String(errorData.non_field_errors)
          } else {
            // Get first field error found
            const firstErrorKey = Object.keys(errorData)[0]
            if (firstErrorKey) {
              const firstError = errorData[firstErrorKey]
              errorMessage = Array.isArray(firstError) ? firstError[0] : String(firstError)
            } else {
              // No specific error message, use status code info
              const status = error.response.status
              if (status === 500) {
                errorMessage = 'Server error (500). Please contact support or try again later.'
              } else if (status === 400) {
                errorMessage = 'Invalid request. Please check your input and try again.'
              } else if (status === 401) {
                errorMessage = 'Unauthorized. Please log in again.'
              } else if (status === 403) {
                errorMessage = 'You do not have permission to perform this action.'
              } else if (status === 404) {
                errorMessage = 'Resource not found.'
              } else {
                errorMessage = `Request failed with status code ${status}`
              }
            }
          }
        } else {
          // No data in response, use status code
          const status = error.response.status
          if (status === 500) {
            errorMessage = 'Server error (500). Please contact support or try again later.'
          } else {
            errorMessage = `Request failed with status code ${status}`
          }
        }
      } else if (error.message) {
        // Use Axios error message (e.g., "Request failed with status code 500")
        errorMessage = String(error.message)
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your connection and try again.'
      }
      
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Add Products</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Products</li>
            <li className="breadcrumb-item active">Add Products</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  <i className="bi bi-box-seam me-2"></i>
                  Add New Product
                </h5>

                <form onSubmit={handleSubmit}>
                  <div className="row mb-3">
                    <label htmlFor="name" className="col-sm-2 col-form-label">Product Name <span className="text-danger">*</span></label>
                    <div className="col-sm-10">
                      <input
                        type="text"
                        className="form-control"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter product name"
                        disabled={loading || fetchingData}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="sku" className="col-sm-2 col-form-label">SKU <span className="text-danger">*</span></label>
                    <div className="col-sm-10">
                      <input
                        type="text"
                        className="form-control"
                        id="sku"
                        name="sku"
                        value={formData.sku}
                        onChange={handleChange}
                        required
                        placeholder="Enter product SKU"
                        disabled={loading || fetchingData}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="category" className="col-sm-2 col-form-label">Category</label>
                    <div className="col-sm-10">
                      <Select
                        id="category"
                        name="category"
                        value={selectedCategory}
                        onChange={(option) => handleSelectChange(option, 'category')}
                        options={categoryOptions}
                        isClearable
                        isSearchable
                        placeholder="Select Category (Optional)"
                        isDisabled={loading || fetchingData}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            borderColor: '#ced4da',
                            '&:hover': {
                              borderColor: '#ced4da'
                            }
                          })
                        }}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="unit" className="col-sm-2 col-form-label">Unit</label>
                    <div className="col-sm-10">
                      <Select
                        id="unit"
                        name="unit"
                        value={selectedUnit}
                        onChange={(option) => handleSelectChange(option, 'unit')}
                        options={unitOptions}
                        isClearable
                        isSearchable
                        placeholder="Select Unit (Optional)"
                        isDisabled={loading || fetchingData}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            borderColor: '#ced4da',
                            '&:hover': {
                              borderColor: '#ced4da'
                            }
                          })
                        }}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="initial_quantity" className="col-sm-2 col-form-label">Initial Quantity </label>
                    <div className="col-sm-10">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        id="initial_quantity"
                        name="initial_quantity"
                        value={formData.initial_quantity}
                        onChange={handleChange}
        
                        placeholder="Enter initial stock quantity"
                        disabled={loading || fetchingData}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="initial_purchase_price" className="col-sm-2 col-form-label">Purchase Price</label>
                    <div className="col-sm-10">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        id="initial_purchase_price"
                        name="initial_purchase_price"
                        value={formData.initial_purchase_price}
                        onChange={handleChange}
                        placeholder="Enter purchase price per unit (Optional)"
                        disabled={loading || fetchingData}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="initial_selling_price" className="col-sm-2 col-form-label">Selling Price</label>
                    <div className="col-sm-10">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className="form-control"
                        id="initial_selling_price"
                        name="initial_selling_price"
                        value={formData.initial_selling_price}
                        onChange={handleChange}
                        placeholder="Enter selling price per unit"
                        disabled={loading || fetchingData}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="supplier_id" className="col-sm-2 col-form-label">Supplier</label>
                    <div className="col-sm-10">
                      <Select
                        id="supplier_id"
                        name="supplier_id"
                        value={selectedSupplier}
                        onChange={(option) => handleSelectChange(option, 'supplier_id')}
                        options={supplierOptions}
                        isClearable
                        isSearchable
                        placeholder="Select Supplier (Optional)"
                        isDisabled={loading || fetchingData}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: '38px',
                            borderColor: '#ced4da',
                            '&:hover': {
                              borderColor: '#ced4da'
                            }
                          })
                        }}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <label htmlFor="image" className="col-sm-2 col-form-label">Product Image</label>
                    <div className="col-sm-10">
                      <input
                        type="file"
                        className="form-control"
                        id="image"
                        name="image"
                        accept="image/*"
                        onChange={handleImageChange}
                        disabled={loading || fetchingData}
                      />
                      <small className="form-text text-muted">
                        Optional. Accepted formats: JPG, PNG, GIF. Max size: 5MB
                      </small>
                      {formData.image && (
                        <div className="mt-2">
                          <img 
                            src={URL.createObjectURL(formData.image)} 
                            alt="Preview" 
                            style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain', border: '1px solid #ddd', borderRadius: '4px', padding: '4px' }}
                          />
                          <div className="mt-1">
                            <small className="text-muted">{formData.image.name}</small>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger ms-2"
                              onClick={() => {
                                setFormData({ ...formData, image: null })
                                const imageInput = document.getElementById('image')
                                if (imageInput) imageInput.value = ''
                              }}
                              disabled={loading || fetchingData}
                            >
                              <i className="bi bi-x-circle me-1"></i>
                              Remove
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-sm-10 offset-sm-2">
                      <button 
                        type="submit" 
                        className="btn btn-primary"
                        disabled={loading || fetchingData}
                      >
                        {loading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Creating...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-plus-circle me-2"></i>
                            Add Product
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default AddProducts

